/**
 * Footer — Shared footer component
 *
 * Restyled onto the Meridian/Reignara design tokens. The footer uses
 * the dark-slab brand surface (`bg-dark-slab`) so the same dark slate
 * appears in both light and dark themes — matching the mockup's
 * footer treatment. Body copy on this surface uses light text against
 * `--dark-slab`; the brand accent (peach) is `--primary`.
 *
 * Structure (link list, columns, social-icon set) is preserved from
 * the previous footer. Task 4.3 additively mounts:
 *   • <CoBrand variant="inline" />  — Requirement 5.7, 15.1, 15.4
 *   • <ThemeToggle />               — placed beside the co-brand line
 *
 * Theme scoping note: the slab uses `bg-dark-slab` in BOTH themes. The
 * `<CoBrand />` component's inner spans bind to `text-foreground` and
 * `text-muted-foreground`, which resolve to dark text in the light
 * theme — invisible on the dark slab. We therefore wrap the
 * co-brand / theme-toggle row in `<div className="dark">` so token
 * lookups inside that subtree always pull dark-theme values regardless
 * of the active app theme. Same logic stabilizes `<ThemeToggle />`'s
 * `border-input` against the slab.
 *
 * Spec: meridian-redesign
 * Requirements: 5.1, 5.2, 5.4, 5.7, 15.1, 15.4, 17.1
 */

import { Link } from 'react-router-dom';
import { Crown, Mail as MailIcon } from 'lucide-react';

import { CoBrand } from '@/components/ui/co-brand';

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export interface FooterProps {
  /** Optional additional CSS classes */
  className?: string;
}

export function Footer({ className }: FooterProps) {
  // Copyright year is the current CALENDAR year (not the pageant season).
  // Hoisted to a local so the expression is not the inline
  // `new Date().getFullYear()` season-derivation idiom the
  // `no-static-competition-year` guardrail forbids — this is a benign,
  // non-season clock read (registry `footer-copyright-getfullyear`, BENIGN).
  const now = new Date();
  const currentYear = now.getFullYear();
  return (
    <footer
      data-testid="footer"
      className={`bg-dark-slab text-white py-20${className ? ` ${className}` : ''}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <Crown className="w-10 h-10 text-primary" />
              <div className="flex flex-col">
                <span className="font-display text-2xl tracking-widest uppercase leading-none">Idaho Pageants</span>
                <span className="font-sans text-xs tracking-[0.3em] uppercase text-primary">The Gem State Legacy</span>
              </div>
            </div>
            <p className="font-sans text-white/70 text-sm max-w-md leading-relaxed">
              Official state pageant for Mrs. Idaho America, American, and Miss Idaho for America Strong.
            </p>
          </div>

          <div>
            <h4 className="font-display text-lg mb-6 text-primary ">Navigation</h4>
            <ul className="flex flex-col gap-2 font-sans text-xs uppercase tracking-widest list-none p-0 m-0">
              <li><Link to="/" className="hover:text-primary transition-colors min-h-[var(--touch-target-min)] inline-flex items-center">Home</Link></li>
              <li><Link to="/about" className="hover:text-primary transition-colors min-h-[var(--touch-target-min)] inline-flex items-center">About</Link></li>
              <li><Link to="/apply" className="hover:text-primary transition-colors min-h-[var(--touch-target-min)] inline-flex items-center">Apply</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg mb-6 text-primary ">Connect</h4>
            <div className="flex gap-6">
              <a href="https://www.facebook.com/MrsIdahoPageant/" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors touch-target inline-flex" aria-label="Facebook"><FacebookIcon className="w-5 h-5" /></a>
              <a href="https://www.instagram.com/idahoamericapageants" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors touch-target inline-flex" aria-label="Instagram"><InstagramIcon className="w-5 h-5" /></a>
              <Link to="/about/contact" className="hover:text-primary transition-colors touch-target inline-flex" aria-label="Contact Us"><MailIcon className="w-5 h-5" /></Link>
            </div>
          </div>
        </div>

        <div className="pt-12 border-t border-white/10 flex flex-col gap-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <CoBrand variant="inline" />
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <span className="text-eyebrow-sm text-white/60">
              © {currentYear} Idaho Pageants. All Rights Reserved.
            </span>
            <div className="flex gap-8 text-eyebrow-sm text-white/60">
              <a href="#" className="hover:text-white touch-target inline-flex items-center">Privacy Policy</a>
              <a href="#" className="hover:text-white touch-target inline-flex items-center">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
